// Blokken "Extra werken" (nieuw) en "Aanrekenbare kosten aan onderaannemer" (SPEC_wijzigingen 3–4).
// Elke regel: P-nummer (standaard dit project), categorie, omschrijving, bedrag, telt mee in
// percentage (verplicht, zonder voorselectie). Kosten hebben een reden; extra werken optioneel de
// oorspronkelijke onderaannemer (3.4).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AANREKENBARE_KOST_CATEGORIEEN,
  CATEGORIE_LABELS,
  EXTRA_WERK_CATEGORIEEN,
  isEigenProject,
  normalizePNummer,
  valideerExtraRegel,
  type ExtraRegel,
  type ExtraRegelSoort,
} from '@recotex/shared';
import { api, type PloegMetPrijslijsten } from '../lib/api';
import { fmtEUR, fmtDate } from '../lib/format';
import { BouwflowProjectPicker } from './BouwflowPicker';
import { BouwflowVoorstelModal } from './BouwflowVoorstelModal';
import { OpleveringChip, OpleveringKeuze } from './CalculatieExtras';

function nieuweRegel(soort: ExtraRegelSoort): ExtraRegel {
  return {
    id: 'tmp_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4),
    soort,
    categorie: soort === 'extra_werk' ? 'OVERIG' : 'SCHADE',
    omschrijving: '',
    bedrag: 0,
    toegerekendProject: '',
    teltMeeInPercentage: null,
    bron: 'manueel',
    bronReferentie: null,
    reden: '',
    oorspronkelijkePloegId: null,
  };
}

export function ExtraRegelsSection({
  soort,
  regels,
  onChange,
  editing,
  projectNummer,
  ploegId,
  ploegen,
  magBevestigen,
  toonFouten,
}: {
  soort: ExtraRegelSoort;
  /** Alle extra regels van de calculatie — dit blok toont/bewerkt enkel die van zijn soort. */
  regels: ExtraRegel[];
  onChange: (regels: ExtraRegel[]) => void;
  editing: boolean;
  /** P-nummer van de calculatie zelf. */
  projectNummer: string;
  /** Onderaannemer van de calculatie (voor de controle op dubbele betaling). */
  ploegId: string;
  ploegen: PloegMetPrijslijsten[];
  magBevestigen: boolean;
  /** Toon validatiefouten (na een opslagpoging). */
  toonFouten?: boolean;
}) {
  const eigen = regels.filter((r) => r.soort === soort);
  const extraWerk = soort === 'extra_werk';
  const som = eigen.reduce((s, r) => s + (r.bedrag || 0), 0);
  const titel = extraWerk ? 'Extra werken' : 'Aanrekenbare kosten aan onderaannemer';
  const kleur = extraWerk ? 'text-accent' : 'text-danger';

  const update = (id: string, patch: Partial<ExtraRegel>) => onChange(regels.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: string) => onChange(regels.filter((r) => r.id !== id));
  // Standaard "dit project" (''), zodat de regel meegaat als het P-nummer van de calculatie wijzigt.
  const add = () => onChange([...regels, nieuweRegel(soort)]);

  if (!editing && eigen.length === 0) return null;

  return (
    <div className={`card mb-5 border-l-4 ${extraWerk ? 'border-accent' : 'border-danger'}`}>
      <div className="flex justify-between items-center mb-2 gap-3 flex-wrap">
        <h3 className={`text-lg font-bold ${kleur}`}>{titel}</h3>
        <div className="flex items-center gap-3">
          <span className="text-sm text-secondary normal-case tracking-normal">
            {extraWerk ? 'Extra werken, totaalbedrag' : 'Totaal aanrekenbare kosten'}:{' '}
            <strong className={kleur}>{extraWerk || som === 0 ? '' : '−'}{fmtEUR(som)}</strong>
          </span>
          {editing && <button className="btn-ghost btn-sm" onClick={add}>+ {extraWerk ? 'Extra werk' : 'Kostenpost'}</button>}
        </div>
      </div>
      <p className="text-xs text-secondary mb-3 normal-case tracking-normal">
        {extraWerk
          ? 'Forfaitaire bedragen voor werk buiten de standaardformule (correctie, herstel, werk van een andere onderaannemer…). Extra m² crepi van dit project horen bij meerwerk, niet hier: die gaan door de schijvenladder.'
          : 'Worden afgetrokken van wat de onderaannemer factureert: schade, franchise van de verzekering, commerciële tegemoetkoming, boete, minwerken.'}
      </p>
      {eigen.length === 0 ? (
        <p className="text-secondary text-sm normal-case tracking-normal">{extraWerk ? 'Geen extra werken.' : 'Geen aanrekenbare kosten.'}</p>
      ) : editing ? (
        <div className="space-y-3">
          {eigen.map((r) => (
            <RegelEditor
              key={r.id}
              regel={r}
              projectNummer={projectNummer}
              ploegId={ploegId}
              ploegen={ploegen}
              magBevestigen={magBevestigen}
              toonFouten={!!toonFouten}
              onUpdate={(patch) => update(r.id, patch)}
              onRemove={() => remove(r.id)}
            />
          ))}
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-xs uppercase tracking-allcaps border-b border-border">
              <th className="text-left px-3 py-2 w-44">Categorie</th>
              <th className="text-left px-3 py-2">Omschrijving</th>
              <th className="text-left px-3 py-2 w-28">Project</th>
              <th className="text-left px-3 py-2 w-28">Percentage</th>
              <th className="text-right px-3 py-2 w-32">Bedrag</th>
            </tr>
          </thead>
          <tbody>
            {eigen.map((r) => {
              const anderProject = !isEigenProject(r, projectNummer);
              return (
                <tr key={r.id} className="border-b border-lightgrey align-top normal-case tracking-normal">
                  <td className="px-3 py-2 text-sm">{CATEGORIE_LABELS[r.categorie] ?? r.categorie}</td>
                  <td className="px-3 py-2 text-sm">
                    {r.omschrijving}
                    {!extraWerk && r.reden && r.reden !== r.omschrijving && <div className="text-xs text-secondary">Reden: {r.reden}</div>}
                    {r.oorspronkelijkePloegId && (
                      <div className="text-xs text-secondary">Werk van: {ploegen.find((p) => p.id === r.oorspronkelijkePloegId)?.team ?? '—'}</div>
                    )}
                    {r.bron === 'bouwflow' && <span className="chip-neutral mt-1 inline-block">uit Bouwflow</span>}
                    {extraWerk && <OpleveringChip oplevering={r.oplevering ?? undefined} />}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {anderProject ? <span className="chip-warning" title="Toegerekend aan een ander project">{normalizePNummer(r.toegerekendProject)}</span> : <span className="text-secondary">dit project</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.teltMeeInPercentage ? 'telt mee' : <span className="text-secondary">telt niet mee</span>}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${kleur}`}>{extraWerk ? '' : '−'}{fmtEUR(r.bedrag)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RegelEditor({
  regel: r,
  projectNummer,
  ploegId,
  ploegen,
  magBevestigen,
  toonFouten,
  onUpdate,
  onRemove,
}: {
  regel: ExtraRegel;
  projectNummer: string;
  ploegId: string;
  ploegen: PloegMetPrijslijsten[];
  magBevestigen: boolean;
  toonFouten: boolean;
  onUpdate: (patch: Partial<ExtraRegel>) => void;
  onRemove: () => void;
}) {
  const extraWerk = r.soort === 'extra_werk';
  const categorieen = extraWerk ? EXTRA_WERK_CATEGORIEEN : AANREKENBARE_KOST_CATEGORIEEN;
  const [zoekProject, setZoekProject] = useState(false);
  const [voorstel, setVoorstel] = useState(false);
  const anderProject = !isEigenProject(r, projectNummer);
  const doel = normalizePNummer(r.toegerekendProject) || normalizePNummer(projectNummer);
  const fouten = toonFouten ? valideerExtraRegel(r) : [];
  const vast = r.bron === 'verrekening_6_5';

  return (
    <div className={`rounded-md border p-3 ${fouten.length ? 'border-danger' : 'border-border'} normal-case tracking-normal`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-3">
          <label className="field-label">Categorie</label>
          <select className="select w-full" value={r.categorie} onChange={(e) => onUpdate({ categorie: e.target.value })}>
            {categorieen.map((c) => <option key={c} value={c}>{CATEGORIE_LABELS[c] ?? c}</option>)}
          </select>
        </div>
        <div className="md:col-span-6">
          <label className="field-label">Omschrijving</label>
          <input className="input" value={r.omschrijving} placeholder={extraWerk ? 'Wat werd er gedaan?' : 'Waarvoor wordt dit aangerekend?'} onChange={(e) => onUpdate({ omschrijving: e.target.value })} />
        </div>
        <div className="md:col-span-3">
          <label className="field-label">Bedrag excl. btw</label>
          <span className="flex items-center gap-1">
            {!extraWerk && <span className="text-danger font-bold">−</span>}
            <input
              type="number" step="0.01" min="0"
              className="input text-right tabular-nums"
              value={r.bedrag || ''}
              onChange={(e) => onUpdate({ bedrag: Math.abs(parseFloat(e.target.value) || 0) })}
            />
            <span className="text-sm">EUR</span>
          </span>
        </div>

        {!extraWerk && (
          <div className="md:col-span-12">
            <label className="field-label">Reden (verplicht)</label>
            <input className="input" value={r.reden} placeholder="Waarom wordt dit aan de onderaannemer aangerekend?" onChange={(e) => onUpdate({ reden: e.target.value })} />
          </div>
        )}

        <div className="md:col-span-5">
          <label className="field-label">Toegerekend aan project</label>
          {zoekProject ? (
            <BouwflowProjectPicker
              value={null}
              placeholder="Zoek op P-nummer of klantnaam…"
              onChange={(sel) => {
                if (sel) {
                  const p = normalizePNummer(sel.projectId);
                  onUpdate({ toegerekendProject: p === normalizePNummer(projectNummer) ? '' : p });
                }
                setZoekProject(false);
              }}
            />
          ) : (
            <div className="flex items-center gap-2">
              <input
                className="input"
                value={r.toegerekendProject}
                placeholder={normalizePNummer(projectNummer) || 'dit project'}
                onChange={(e) => onUpdate({ toegerekendProject: e.target.value.toUpperCase() })}
                onBlur={(e) => {
                  const p = normalizePNummer(e.target.value);
                  onUpdate({ toegerekendProject: p === normalizePNummer(projectNummer) ? '' : p });
                }}
              />
              <button type="button" className="btn-ghost btn-sm whitespace-nowrap" onClick={() => setZoekProject(true)} title="Zoek het project in Bouwflow">Zoek</button>
              {anderProject && (
                <button type="button" className="btn-ghost btn-sm whitespace-nowrap" onClick={() => onUpdate({ toegerekendProject: '' })}>Dit project</button>
              )}
            </div>
          )}
          <p className="field-hint">
            Kies het project waar dit {extraWerk ? 'werk' : 'bedrag'} bij hoort.
            {extraWerk && ' Werd het extra werk aan de klant verkocht onder een apart P-nummer, kies dan dat P-nummer, zodat kost en verkoopwaarde in hetzelfde percentage zitten.'}
          </p>
        </div>

        <fieldset className="md:col-span-4">
          <legend className="field-label">Telt mee in percentage van {doel || 'dit project'}?</legend>
          <div className="flex gap-2">
            {[true, false].map((v) => (
              <label key={String(v)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md border cursor-pointer text-sm ${r.teltMeeInPercentage === v ? 'border-primary bg-primary-50 font-bold' : 'border-border'} ${vast ? 'opacity-60 cursor-not-allowed' : ''}`}>
                <input type="radio" name={`teltmee-${r.id}`} checked={r.teltMeeInPercentage === v} disabled={vast} onChange={() => onUpdate({ teltMeeInPercentage: v })} />
                {v ? 'Ja' : 'Nee'}
              </label>
            ))}
          </div>
          {vast && <p className="field-hint">Verrekening uit een correctiecalculatie: telt vast niet mee, de kost staat al op het andere project.</p>}
        </fieldset>

        {extraWerk && (
          <div className="md:col-span-3">
            <label className="field-label">Oorspronkelijke onderaannemer</label>
            <select className="select w-full" value={r.oorspronkelijkePloegId ?? ''} onChange={(e) => onUpdate({ oorspronkelijkePloegId: e.target.value || null })}>
              <option value="">— niet van toepassing —</option>
              {ploegen.filter((p) => p.id !== ploegId).map((p) => <option key={p.id} value={p.id}>{p.team} · {p.firma}</option>)}
            </select>
            <p className="field-hint">Wiens werk werd afgewerkt of gecorrigeerd?</p>
          </div>
        )}
      </div>

      {extraWerk && anderProject && doel && <DubbeleBetalingControle projectNummer={doel} ploegId={ploegId} />}

      {fouten.length > 0 && (
        <ul className="text-danger text-xs mt-2 list-disc list-inside">{fouten.map((f) => <li key={f}>{f}</li>)}</ul>
      )}

      {extraWerk && (
        // Open voor oplevering (27okt 4): het extra werk staat op de calculatie maar mag nog niet gefactureerd worden.
        <div className="mt-3">
          <OpleveringKeuze oplevering={r.oplevering ?? undefined} onChange={(o) => onUpdate({ oplevering: o ?? null })} />
        </div>
      )}

      <div className="flex justify-between items-center mt-3 gap-2 flex-wrap">
        <span className="text-xs text-secondary">
          {r.bron === 'bouwflow' ? `Bedrag uit Bouwflow${r.bronReferentie ? ` (${r.bronReferentie})` : ''}, nadien manueel aanpasbaar` : r.bron === 'verrekening_6_5' ? 'Automatische verrekenregel' : 'Manueel bedrag'}
        </span>
        <div className="flex gap-2">
          {extraWerk && doel && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => setVoorstel(true)} title="Bereken het bedrag uit de opstartfiche van het gelinkte project">Voorstel uit Bouwflow</button>
          )}
          <button type="button" className="btn-ghost btn-sm" onClick={onRemove}>Verwijderen</button>
        </div>
      </div>

      {voorstel && (
        <BouwflowVoorstelModal
          projectNummer={doel}
          ploegId={ploegId}
          magBevestigen={magBevestigen}
          onSluit={() => setVoorstel(false)}
          onBevestig={(v) => {
            onUpdate({ bedrag: v.bedrag, bron: 'bouwflow', bronReferentie: v.bronReferentie, omschrijving: r.omschrijving || v.omschrijving });
            setVoorstel(false);
          }}
        />
      )}
    </div>
  );
}

/** Controle op dubbele betaling (3.3): bestaande calculaties van het gelinkte project voor dezelfde onderaannemer. */
function DubbeleBetalingControle({ projectNummer, ploegId }: { projectNummer: string; ploegId: string }) {
  const q = useQuery({
    queryKey: ['dubbele-betaling', projectNummer, ploegId],
    queryFn: () => api.projects.list({ projectNummer, ploegId, limit: 20 }),
  });
  const calcs = (q.data?.projects ?? []).filter((p) => normalizePNummer(p.projectNummer) === projectNummer);
  if (!calcs.length) return null;
  return (
    <div className="mt-3 p-3 rounded-md bg-warning-100 border-l-4 border-warning text-sm">
      <strong>Controleer dat dit werk niet al op een andere calculatie betaald werd.</strong>
      <ul className="mt-1 text-xs space-y-0.5">
        {calcs.map((c) => (
          <li key={c.id}>
            <a href={`/project/${c.id}`} target="_blank" rel="noopener noreferrer" className="underline">{c.projectNummer} · {c.klantNaam}</a>
            {' '}— {fmtDate(c.createdAt)} — {fmtEUR(c.totaal)}
          </li>
        ))}
      </ul>
    </div>
  );
}
